/**
 * Screen editing (Phase 3) split into a data hook + a detail view so the
 * screen LIST can live in the Design tab's single left nav (alongside
 * sections) while the per-screen editor renders in the centre panel.
 *
 * `useScreens` owns the list + CRUD; `ScreenDetail` is the per-screen form +
 * live SVG preview. Saving regenerates the `.svg` renderer-side (TDD §8.3) and
 * writes both files via `design:writeScreen`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { Screen, Zone } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';
import { deleteScreen, listScreens, writeScreen } from './anvil-api';
import { renderScreenSvg } from './lib/screen-svg';
import { ZONE_KINDS } from './lib/zone-kinds';
import { ScreenCanvas } from './ScreenCanvas';
import { TagsField } from './TagsField';

let counter = 0;
function newId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now()}-${counter}`;
}

/** Consecutive edits within this window collapse into ONE undo step, so a drag
 *  (many `update()`s) or a burst of typing is a single undo, not one-per-pixel. */
const COALESCE_MS = 600;
const HISTORY_LIMIT = 50;

function capHistory(stack: Screen[]): Screen[] {
  return stack.length > HISTORY_LIMIT ? stack.slice(stack.length - HISTORY_LIMIT) : stack;
}

/** Content key for de-duping history entries (ignores server-stamped timestamps). */
function screenKey(s: Screen): string {
  return JSON.stringify({
    name: s.name,
    surface: s.surface,
    description: s.description,
    zones: s.zones,
    linkedSectionIds: s.linkedSectionIds,
  });
}

function blankScreen(): Screen {
  return {
    id: newId('screen'),
    name: 'New Screen',
    surface: 'tv',
    description: '',
    zones: [],
    linkedSectionIds: [],
    createdAt: '',
    updatedAt: '',
  };
}

export interface ScreensState {
  screens: Screen[];
  loaded: boolean;
  /** Persist a screen (create or update); returns the written record. */
  save: (screen: Screen) => Promise<Screen>;
  remove: (id: string) => Promise<void>;
  /** Create a blank screen and persist it; returns the new record. */
  create: () => Promise<Screen>;
}

export function useScreens(gameId: string): ScreensState {
  const client = useCrucibleClient();
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    listScreens(client, gameId)
      .then((list) => {
        if (!cancelled) {
          setScreens(list);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, client]);

  const save = useCallback(
    async (screen: Screen): Promise<Screen> => {
      const written = await writeScreen(client, gameId, screen, renderScreenSvg(screen));
      setScreens((prev) =>
        prev.some((s) => s.id === written.id)
          ? prev.map((s) => (s.id === written.id ? written : s))
          : [...prev, written],
      );
      return written;
    },
    [gameId, client],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await deleteScreen(client, gameId, id);
      setScreens((prev) => prev.filter((s) => s.id !== id));
    },
    [gameId, client],
  );

  const create = useCallback((): Promise<Screen> => save(blankScreen()), [save]);

  return { screens, loaded, save, remove, create };
}

export interface ScreenDetailProps {
  screen: Screen;
  onChange: (screen: Screen) => void;
  onDelete: () => void;
  /** Known tags across the design (most-used first) — TagsField autocomplete. */
  tagSuggestions?: string[];
}

/**
 * Per-screen editor: an interactive scale-to-fit canvas (select / move / resize
 * / draw zones) plus a compact editor for the selected zone. Edits mutate a
 * local working copy for smooth dragging and persist on a short debounce, so a
 * drag is one save — not one per mouse-move.
 */
export function ScreenDetail({
  screen,
  onChange,
  onDelete,
  tagSuggestions,
}: ScreenDetailProps): ReactElement {
  const [working, setWorking] = useState<Screen>(screen);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  // Undo/redo history of the editable screen. `stack[index]` is the current
  // committed state; edits push (truncating any redo tail), coalescing a
  // gesture into one entry. Kept separate from `working` so dragging stays
  // smooth (working updates every mouse-move; history commits per gesture).
  const [hist, setHist] = useState<{ stack: Screen[]; index: number }>({
    stack: [screen],
    index: 0,
  });
  const lastEditAt = useRef(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSave = useRef<Screen | null>(null);
  const onChangeRef = useRef(onChange);

  const canUndo = hist.index > 0;
  const canRedo = hist.index < hist.stack.length - 1;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const flushPendingSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const pending = pendingSave.current;
    if (!pending) return;
    pendingSave.current = null;
    onChangeRef.current(pending);
  }, []);

  // Re-sync + reset history when switching to a different screen (not on
  // same-screen saves, which only bump updatedAt and would clobber an edit).
  useEffect(() => {
    setWorking(screen);
    setSelectedZoneId(null);
    setHist({ stack: [screen], index: 0 });
    lastEditAt.current = 0;
    return () => flushPendingSave();
  }, [screen.id, flushPendingSave]);

  const update = useCallback((next: Screen) => {
    setWorking(next);
    const now = Date.now();
    const coalesce = now - lastEditAt.current < COALESCE_MS;
    lastEditAt.current = now;
    setHist((h) => {
      const base = h.stack.slice(0, h.index + 1);
      // Coalesce a continuing gesture into the top entry — but never into the
      // baseline at index 0, which is the pre-edit state to undo back to.
      if (coalesce && base.length > 1) {
        const stack = capHistory([...base.slice(0, -1), next]);
        return { stack, index: stack.length - 1 };
      }
      if (base.length > 0 && screenKey(base[base.length - 1]) === screenKey(next)) return h;
      const stack = capHistory([...base, next]);
      return { stack, index: stack.length - 1 };
    });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    pendingSave.current = next;
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      const pending = pendingSave.current;
      pendingSave.current = null;
      if (pending) onChangeRef.current(pending);
    }, 300);
  }, []);

  // Jump to a history entry (undo/redo): set working, persist immediately,
  // drop any pending debounced save, and keep the selection only if it still
  // exists in the target state.
  const applyHistory = useCallback((target: Screen, nextIndex: number) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    pendingSave.current = null;
    lastEditAt.current = 0; // the next edit starts a fresh history entry
    setHist((h) => ({ ...h, index: nextIndex }));
    setWorking(target);
    setSelectedZoneId((cur) => (target.zones.some((z) => z.id === cur) ? cur : null));
    onChangeRef.current(target);
  }, []);

  const undo = useCallback(() => {
    if (hist.index <= 0) return;
    applyHistory(hist.stack[hist.index - 1], hist.index - 1);
  }, [hist, applyHistory]);

  const redo = useCallback(() => {
    if (hist.index >= hist.stack.length - 1) return;
    applyHistory(hist.stack[hist.index + 1], hist.index + 1);
  }, [hist, applyHistory]);

  // ⌘Z / ⇧⌘Z (and Ctrl variants). Skipped when focus is in a text field so the
  // browser's native text-undo still works there.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const selectedZone = working.zones.find((z) => z.id === selectedZoneId) ?? null;
  const patchZone = (p: Partial<Zone>): void => {
    if (!selectedZone) return;
    update({
      ...working,
      zones: working.zones.map((z) => (z.id === selectedZone.id ? { ...z, ...p } : z)),
    });
  };
  const deleteZone = (): void => {
    if (!selectedZone) return;
    update({ ...working, zones: working.zones.filter((z) => z.id !== selectedZone.id) });
    setSelectedZoneId(null);
  };
  const bringToFront = (): void => {
    const max = working.zones.reduce((m, z) => Math.max(m, z.zIndex ?? 0), 0);
    patchZone({ zIndex: max + 1 });
  };
  const sendToBack = (): void => {
    const min = working.zones.reduce((m, z) => Math.min(m, z.zIndex ?? 0), 0);
    patchZone({ zIndex: min - 1 });
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col" data-testid="design-screen-detail">
      <header className="flex items-center gap-2 border-b border-crucible-border px-4 py-2">
        <input
          value={working.name}
          onChange={(e) => update({ ...working, name: e.target.value })}
          className="flex-1 bg-transparent text-sm text-crucible-fg outline-none"
        />
        <select
          value={working.surface}
          onChange={(e) => update({ ...working, surface: e.target.value as Screen['surface'] })}
          className="bg-transparent text-xs text-crucible-fg outline-none"
        >
          <option value="tv">TV</option>
          <option value="phone">Phone</option>
        </select>
        <button
          type="button"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (⌘Z)"
          aria-label="Undo"
          data-testid="screen-undo"
          className="rounded border border-crucible-border px-1.5 py-0.5 text-xs text-crucible-muted hover:bg-crucible-bg-elevated disabled:cursor-not-allowed disabled:opacity-30"
        >
          ↶
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (⇧⌘Z)"
          aria-label="Redo"
          data-testid="screen-redo"
          className="rounded border border-crucible-border px-1.5 py-0.5 text-xs text-crucible-muted hover:bg-crucible-bg-elevated disabled:cursor-not-allowed disabled:opacity-30"
        >
          ↷
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-crucible-muted hover:text-red-400"
        >
          Delete screen
        </button>
      </header>
      <div className="border-b border-crucible-border px-4 py-1.5">
        <TagsField
          compact
          tags={working.tags ?? []}
          onCommit={(tags) => update({ ...working, tags })}
          suggestions={tagSuggestions}
        />
      </div>

      <ScreenCanvas
        screen={working}
        onChange={update}
        selectedZoneId={selectedZoneId}
        onSelectZone={setSelectedZoneId}
      />

      {/* Selected-zone editor / hint. */}
      <div className="border-t border-crucible-border px-4 py-2">
        {selectedZone ? (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input
              value={selectedZone.label}
              placeholder="label"
              onChange={(e) => patchZone({ label: e.target.value })}
              className="w-40 rounded border border-crucible-border bg-transparent px-2 py-1 text-crucible-fg outline-none"
            />
            <select
              value={selectedZone.kind}
              onChange={(e) => patchZone({ kind: e.target.value })}
              className="rounded border border-crucible-border bg-transparent px-1 py-1 text-crucible-fg outline-none"
            >
              {ZONE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            {(['x', 'y', 'width', 'height'] as const).map((dim) => (
              <label key={dim} className="flex items-center gap-1 text-crucible-muted">
                {dim[0]}
                <input
                  type="number"
                  value={Math.round(selectedZone[dim])}
                  onChange={(e) => patchZone({ [dim]: Number(e.target.value) })}
                  className="w-16 rounded border border-crucible-border bg-transparent px-1 py-1 text-crucible-fg outline-none"
                />
              </label>
            ))}
            <span className="flex items-center gap-1 text-crucible-muted">
              z {selectedZone.zIndex ?? 0}
              <button
                type="button"
                onClick={bringToFront}
                title="Bring to front"
                className="rounded border border-crucible-border px-1.5 py-0.5 hover:bg-crucible-bg-elevated"
              >
                Front
              </button>
              <button
                type="button"
                onClick={sendToBack}
                title="Send to back"
                className="rounded border border-crucible-border px-1.5 py-0.5 hover:bg-crucible-bg-elevated"
              >
                Back
              </button>
            </span>
            <button
              type="button"
              onClick={deleteZone}
              className="text-crucible-muted hover:text-red-400"
            >
              Delete zone
            </button>
          </div>
        ) : (
          <p className="text-xs text-crucible-muted">
            Click a zone to edit it, drag to move, use the corner handles to resize — or drag on an
            empty area to draw a new zone.
          </p>
        )}
      </div>
    </div>
  );
}
